<template>
  <div id="event-entity-view">
    <main class="entity-page">
      <v-container v-if="details">
        <div class="back-links mb-6">
          <v-btn outlined class="back-link" :to="eventRoute">
            <v-icon left>mdi-arrow-left</v-icon>{{ event.title }}
          </v-btn>
          <v-btn v-if="showEatsLink" outlined class="back-link" :to="{ name: 'reckon-way-eats' }">
            <v-icon left>mdi-silverware-fork-knife</v-icon>Back to Eats
          </v-btn>
        </div>
        <header class="entity-hero">
          <span class="entity-tag">{{ sectionLabel }}</span>
          <h1>{{ details.title }}</h1>
          <p>{{ details.description }}</p>
          <div class="experience-meta">
            <span v-if="details.dateLabel"><v-icon small>mdi-calendar-outline</v-icon>{{ details.dateLabel }}</span>
            <span v-if="details.timeLabel"><v-icon small>mdi-clock-outline</v-icon>{{ details.timeLabel }}</span>
            <span v-if="details.location"><v-icon small>mdi-map-marker-outline</v-icon>{{ details.location }}</span>
          </div>
          <a v-if="activeStorySections.length" class="narrative-link" href="#full-story">
            Read the full narrative <v-icon small>mdi-arrow-down</v-icon>
          </a>
        </header>
        <nav v-if="storyPages.length" class="story-page-nav" aria-label="Experience story pages">
          <router-link
            v-for="page in storyPages"
            :key="page.slug"
            :to="storyPageRoute(page)"
            :class="{ active: currentStoryPage.slug === page.slug }"
          >
            <span>{{ page.label }}</span>
            <strong>{{ page.title }}</strong>
            <small>{{ page.date }}</small>
          </router-link>
        </nav>
        <section v-if="!storyPages.length" class="description-card">
          <div>
            <p class="kicker">{{ currentStoryPage ? currentStoryPage.label : 'The Story' }}</p>
            <h2>{{ currentStoryPage ? currentStoryPage.title : 'What we remember' }}</h2>
          </div>
          <article>
            <p v-if="currentStoryPage" class="story-page-summary">{{ currentStoryPage.summary }}</p>
            <p v-if="currentStoryPage || details.dayLabel" class="detail-meta">{{ currentStoryPage ? currentStoryPage.date : details.dayLabel }}</p>
            <p v-if="details.location" class="detail-meta">{{ details.location }}</p>
            <dl v-if="details.storyItems.length && !currentStoryPage">
              <div v-for="story in details.storyItems" :key="story.label">
                <dt>{{ story.label }}</dt><dd>{{ story.value }}</dd>
              </div>
            </dl>
            <div v-if="details.websiteUrl || details.mapUrl" class="entity-actions">
              <a v-if="details.websiteUrl" :href="details.websiteUrl" target="_blank" rel="noopener noreferrer">Website <v-icon small>mdi-open-in-new</v-icon></a>
              <a v-if="details.mapUrl" :href="details.mapUrl" target="_blank" rel="noopener noreferrer">Map <v-icon small>mdi-map-marker-outline</v-icon></a>
            </div>
          </article>
        </section>
        <section v-if="activeStorySections.length" id="full-story" class="full-story" aria-labelledby="full-story-title">
          <p class="kicker">The Full Story</p>
          <h2 id="full-story-title">{{ storyTitle }}</h2>
          <article v-for="(section, sectionIndex) in activeStorySections" :key="section.heading" class="story-section">
            <h3 v-if="(!isOverviewPage || sectionIndex > 0) && !section.hideHeading">{{ section.heading }}</h3>
            <p v-if="section.subheading" class="story-subheading">{{ section.subheading }}</p>
            <p v-if="section.date" class="story-date">{{ section.date }}</p>
            <div
              v-for="(paragraph, index) in section.paragraphs"
              :key="`${section.heading}-${index}`"
              class="story-paragraph-block"
            >
              <p>
                <template v-for="(segment, segmentIndex) in paragraphSegments(paragraph)">
                  <router-link
                    v-if="segment.to"
                    :key="`${section.heading}-${index}-link-${segmentIndex}`"
                    :to="segment.to"
                  >{{ segment.text }}</router-link>
                  <span v-else :key="`${section.heading}-${index}-text-${segmentIndex}`">{{ segment.text }}</span>
                </template>
              </p>
              <ResponsiveMediaGallery
                v-if="imagesAfterParagraph(section, index).length"
                :items="imagesAfterParagraph(section, index)"
                :aria-label="`${section.heading} gallery`"
                natural-images
                class="story-section-gallery"
              />
            </div>
            <ol v-if="section.items && section.items.length" class="story-list">
              <li v-for="(itemText, index) in section.items" :key="`${section.heading}-item-${index}`">
                {{ itemText }}
              </li>
            </ol>
          </article>
        </section>
        <nav v-if="storyPages.length" class="story-page-nav story-page-nav-footer" aria-label="More experience story pages">
          <router-link
            v-for="page in storyPages"
            :key="`footer-${page.slug}`"
            :to="storyPageRoute(page)"
            :class="{ active: currentStoryPage.slug === page.slug }"
          >
            <span>{{ page.label }}</span>
            <strong>{{ page.title }}</strong>
            <small>{{ page.date }}</small>
          </router-link>
        </nav>
        <nav v-if="adjacentStories.previous || adjacentStories.next" class="journey-navigation" aria-label="Chronological story navigation">
          <router-link v-if="adjacentStories.previous" :to="adjacentStories.previous.route" class="journey-link journey-link-previous">
            <span><v-icon small>mdi-arrow-left</v-icon>Previous story</span>
            <strong>{{ adjacentStories.previous.title }}</strong>
          </router-link>
          <router-link :to="timelineRoute" class="timeline-return">
            <v-icon small>mdi-timeline-outline</v-icon>Trip chronology
          </router-link>
          <router-link v-if="adjacentStories.next" :to="adjacentStories.next.route" class="journey-link journey-link-next">
            <span>Next story<v-icon small>mdi-arrow-right</v-icon></span>
            <strong>{{ adjacentStories.next.title }}</strong>
          </router-link>
        </nav>
      </v-container>
      <EventGallery v-if="details && details.media && details.media.length" :media="details.media" :title="`${details.title} Gallery`" />
      <v-container v-if="!details">
        <section class="missing-state"><h1>Experience not found</h1><p>This dining or activity record does not have a gallery page.</p></section>
      </v-container>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import { experienceEntityById } from '../data/reckon-way/entities';
import { reckonWayEventBySlug } from '../data/reckon-way/events';
import { eventEntityRecordId, eventSectionItems, resolveEventEntityDetails } from '../lib/reckon-way/entityDetails';
import { adjacentEventStories, eventStoryTimeline } from '../lib/reckon-way/storyTimeline';
import AppFooter from '../components/Footer.vue';
import EventGallery from '../components/reckon-way/media/EventGallery.vue';
import ResponsiveMediaGallery from '../components/reckon-way/media/ResponsiveMediaGallery.vue';

export default {
  name: 'EventEntityView',
  components: { AppFooter, EventGallery, ResponsiveMediaGallery },
  computed: {
    event() { return reckonWayEventBySlug[this.$route.params.gallerySlug] || null; },
    item() {
      return eventSectionItems(this.event, this.$route.params.sectionKey)
        .find((candidate) => eventEntityRecordId(candidate) === this.$route.params.entityId);
    },
    entity() { return experienceEntityById[this.item?.entityId] || null; },
    details() {
      return resolveEventEntityDetails({
        event: this.event,
        sectionKey: this.$route.params.sectionKey,
        item: this.item,
        entity: this.entity,
      });
    },
    storyPages() { return this.details?.storyPages || []; },
    currentStoryPage() {
      if (!this.storyPages.length) return null;
      return this.storyPages.find((page) => page.slug === this.$route.params.storyPageSlug)
        || this.storyPages[0];
    },
    activeStorySections() {
      return this.currentStoryPage?.sections || this.details?.storySections || [];
    },
    storyTitle() {
      if (this.isOverviewPage) return this.currentStoryPage.title;
      return this.currentStoryPage
        ? `${this.currentStoryPage.label}: ${this.currentStoryPage.title}`
        : this.details?.storyTitle || this.details?.title;
    },
    isOverviewPage() { return this.currentStoryPage?.slug === 'overview'; },
    sectionLabel() { return this.$route.params.sectionKey === 'dining' ? 'Dining Along the Way' : 'Activities and Team Experiences'; },
    showEatsLink() {
      return this.$route.params.sectionKey === 'dining'
        || ['guinness-storehouse-tour', 'temple-bar-dublin']
          .includes(this.$route.params.entityId);
    },
    adjacentStories() {
      return adjacentEventStories(
        this.event,
        experienceEntityById,
        this.$route.params.sectionKey,
        eventEntityRecordId(this.item),
      );
    },
    timelineEntry() {
      return eventStoryTimeline(this.event, experienceEntityById).find((entry) => (
        entry.sectionKey === this.$route.params.sectionKey
        && entry.recordId === eventEntityRecordId(this.item)
      )) || null;
    },
    eventRoute() { return { name: this.event?.slug === 'aws-reinvent-las-vegas' ? 'aws-reinvent' : 'reckon-way-gallery', params: { gallerySlug: this.event?.slug }, hash: `#${this.$route.params.sectionKey}` }; },
    timelineRoute() {
      return {
        ...this.eventRoute,
        hash: this.timelineEntry ? `#story-${this.timelineEntry.id}` : '#story-timeline',
      };
    },
  },
  methods: {
    paragraphSegments(paragraph) {
      if (typeof paragraph === 'string') return [{ text: paragraph }];
      return paragraph?.segments || [{ text: paragraph?.text || '' }];
    },
    imagesAfterParagraph(section, paragraphIndex) {
      const placement = (section.imagePlacements || [])
        .find((item) => item.afterParagraph === paragraphIndex);
      if (placement) return placement.images || [];

      if (section.imageAfterParagraph === paragraphIndex) return section.images || [];
      return [];
    },
    storyPageRoute(page) {
      const params = {
        gallerySlug: this.$route.params.gallerySlug,
        sectionKey: this.$route.params.sectionKey,
        entityId: this.$route.params.entityId,
      };
      if (page.slug === this.storyPages[0]?.slug) {
        return { name: 'reckon-way-entity', params };
      }
      return { name: 'reckon-way-entity-story-page', params: { ...params, storyPageSlug: page.slug } };
    },
  },
};
</script>

<style scoped>
.entity-page{background:#050505;color:#f5f5f5;min-height:75vh;padding:48px 0 72px}.back-links{display:flex;flex-wrap:wrap;gap:10px}.back-link{border-color:rgba(255,255,255,.3)!important;color:#fff!important}.entity-hero{background:linear-gradient(135deg,rgba(226,50,50,.18),rgba(255,255,255,.04));border:1px solid rgba(226,50,50,.35);border-radius:12px;padding:30px}.entity-tag{background:rgba(232,90,90,.14);border:1px solid rgba(232,90,90,.55);border-radius:999px;color:#f18a8a;display:inline-block;font-size:.68rem;font-weight:900;letter-spacing:.08em;margin-bottom:10px;padding:4px 9px;text-transform:uppercase}h1,h2{color:#fff}h1{font-size:clamp(2rem,5vw,3.4rem);margin:0}.entity-hero>p{color:#d6d6d6;line-height:1.65;margin:10px 0 0;max-width:800px}.experience-meta{display:flex;flex-wrap:wrap;gap:9px 18px;margin-top:18px}.experience-meta span{align-items:center;color:#c9c9c9;display:inline-flex;font-size:.88rem;gap:6px}.experience-meta .v-icon{color:#f0b13c}.narrative-link{align-items:center;color:#f0b13c;display:inline-flex;font-weight:900;gap:7px;margin-top:22px;text-decoration:none;text-transform:uppercase}.narrative-link .v-icon{color:inherit}.story-page-nav{display:grid;gap:14px;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));margin:28px auto 0;max-width:820px}.story-page-nav a{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.15);border-radius:10px;color:#fff;display:flex;flex-direction:column;padding:18px;text-decoration:none;transition:.2s ease}.story-page-nav a:hover,.story-page-nav a.active{background:rgba(226,50,50,.13);border-color:rgba(232,90,90,.7);transform:translateY(-2px)}.story-page-nav span{color:#ef7777;font-size:.7rem;font-weight:900;letter-spacing:.1em;text-transform:uppercase}.story-page-nav strong{font-size:1.05rem;margin:4px 0}.story-page-nav small{color:#aaa}.story-page-nav-footer{margin-top:54px}.description-card{margin:44px auto 0;max-width:820px}.kicker{color:#e85a5a;font-size:.78rem;font-weight:900;letter-spacing:.12em;margin:0 0 6px;text-transform:uppercase}.description-card h2{font-size:1.8rem;margin:0}.description-card article,.missing-state{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.12);border-radius:10px;padding:24px}.description-card article{margin-top:24px}.story-page-summary{color:#e1e1e1;font-size:1.05rem;line-height:1.7;margin:0 0 18px}.detail-meta{color:#d0d0d0;margin:0 0 8px}.description-card dl{border-top:1px solid rgba(255,255,255,.1);display:grid;gap:18px;margin:18px 0 0;padding-top:18px}.description-card dt{color:#f0b13c;font-size:.72rem;font-weight:800;text-transform:uppercase}.description-card dd{color:#c8c8c8;line-height:1.6;margin:4px 0 0}.entity-actions{border-top:1px solid rgba(255,255,255,.1);display:flex;gap:18px;margin-top:18px;padding-top:16px}.entity-actions a{color:#ef7777;font-weight:800;text-decoration:none}.entity-actions .v-icon{color:currentColor}.full-story{margin:64px auto 0;max-width:820px;scroll-margin-top:90px}.full-story>h2{font-size:clamp(1.9rem,4vw,2.8rem);margin:0 0 32px}.story-section{border-top:1px solid rgba(255,255,255,.13);padding:30px 0 10px}.story-section h3{color:#f0b13c;font-size:1.45rem;margin:0 0 16px}.story-section .story-subheading{color:#fff;font-size:1.2rem;font-weight:800;line-height:1.4;margin:-6px 0 8px}.story-section .story-date{color:#aaa;font-style:italic;margin:0 0 24px}.story-section p{color:#d0d0d0;font-size:1.02rem;line-height:1.78;margin:0 0 18px}.story-section p a{color:#f0b13c;font-weight:800;text-decoration:underline;text-underline-offset:3px}.story-section-gallery{margin:8px 0 28px}.story-list{column-gap:42px;columns:2;color:#d0d0d0;line-height:1.65;margin:0;padding-left:24px}.story-list li{break-inside:avoid;margin:0 0 8px;padding-left:5px}.story-list li::marker{color:#e85a5a;font-family:"Oswald",sans-serif;font-weight:900}.journey-navigation{align-items:stretch;border-top:1px solid rgba(255,255,255,.15);display:grid;gap:14px;grid-template-columns:1fr auto 1fr;margin:58px auto 0;max-width:980px;padding-top:28px}.journey-link,.timeline-return{border:1px solid rgba(255,255,255,.16);border-radius:10px;color:#fff;display:flex;padding:16px;text-decoration:none}.journey-link{flex-direction:column}.journey-link-next{text-align:right}.journey-link span{align-items:center;color:#ef7777;display:flex;font-size:.7rem;font-weight:900;gap:5px;letter-spacing:.08em;text-transform:uppercase}.journey-link-next span{justify-content:flex-end}.journey-link strong{font-size:1rem;margin-top:6px}.timeline-return{align-items:center;color:#f0b13c;font-size:.76rem;font-weight:900;gap:6px;justify-content:center;text-transform:uppercase}.journey-link .v-icon,.timeline-return .v-icon{color:inherit}.journey-link:hover,.timeline-return:hover{border-color:#e23232}.missing-state{text-align:center}@media(max-width:700px){.entity-page{padding-top:28px}.story-page-nav{grid-template-columns:1fr}.full-story{margin-top:48px}.story-section p{font-size:1rem}.story-list{columns:1}.journey-navigation{grid-template-columns:1fr}.timeline-return{grid-row:1}.journey-link-next{text-align:left}.journey-link-next span{justify-content:flex-start}}
</style>
